import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { E2eIdDirective } from '../directives/e2e-id.directive';
import { RecoveryCodesPanelComponent } from '../recovery-codes-panel/recovery-codes-panel.component';

/**
 * Standalone show-once recovery-codes dialog (SD-8082) for the Settings **regenerate** path.
 *
 * A thin shell around {@link RecoveryCodesPanelComponent}: header, the panel, and the confirm
 * action gated on the panel's acknowledgement. Enrollment does NOT use this dialog — the setup
 * steps embed the same panel as their final stage, so the codes never open a second surface on
 * top of the setup one.
 *
 * The plaintext codes are returned by the server exactly once, so this is a must-acknowledge
 * gate: it ignores escape/backdrop, and Done stays disabled until the panel's "I have saved these" checkbox.
 */
@Component({
    selector: 'app-recovery-codes-dialog',
    templateUrl: './recovery-codes-dialog.component.html',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        RecoveryCodesPanelComponent,
        E2eIdDirective,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class RecoveryCodesDialogComponent implements OnChanges
{
    @Input() open = false;
    @Input() codes: string[] = [];
    @Output() closed = new EventEmitter<void>();

    /** The confirm button stays disabled until the user affirms they've saved the codes. */
    acknowledged = false;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['open']?.currentValue && !changes['open'].previousValue) {
            this.acknowledged = false;
        }
    }

    /** Close only once the user has confirmed they saved the codes. */
    done(): void {
        if (!this.acknowledged) {
            return;
        }
        this.closed.emit();
    }
}
